import React, { useEffect, useState, useRef } from 'react';
import { PAGES_ROOT } from 'config/api';
import { useSelector, connect } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { withStyles } from 'theme/withStyles';
import uiActions from 'core/ui/actions';
import plaidActions from 'core/plaid/actions';
import BottomContent from 'common/BottomContent';
import ErrorMsg from 'common/ErrorMsg';
import OutputFunding from 'fields/OutputFunding';
import usePlaid from 'hooks/usePlaid';
import ButtonRowGroup from './common/ButtonRowGroup';
import RawHtml from '../common/RawHtml';
import commonStyles from './styles';
import fundingButtonRowStyles from './styles/fundingButtonRow';

const styles = (theme) => {
  const common = commonStyles(theme);

  return {
    ...common,
    actionButtons: {
      '& button': {
        '&:hover': {
          color: theme.links.link.hoverColor,
        },
        background: 'none',
        border: 'none',
        color: theme.links.link.color,
        cursor: 'pointer',
        fontFamily: theme.typography.p.fontFamily,
        fontSize: theme.typography.p1.fontSize,
        padding: 0,
        [`@media(min-width: ${theme.screenSizes.mobileMax}px)`]: {
          fontSize: theme.typography.p1.desktopFontSize,
        },
      },
      '& span': {
        color: theme.palette.form.border,
        margin: [0, 10],
        position: 'relative',
      },
      marginBottom: 30,
      position: 'relative',
      textAlign: 'center',
    },
    disclosureSubtext: {
      '& img': {
        maxWidth: '100%',
      },
    },

    fundingButtonRow: {
      ...fundingButtonRowStyles(theme),
    },
  };
};

const FundingTransferExtBare = ({
  backAllowed,
  backButtonLabel,
  bottomContent,
  children,
  classes,
  fetchAccounts,
  hasFields,
  hasNoFields,
  label,
  labelMobile,
  linkVerification,
  loading,
  name,
  onBack,
  onError,
  refetchAccounts,
  resources,
  setAppLoading,
  subText,
  updateMeta,
}) => {
  const plaidParams = useRef();
  const [plaidSuccess, setPlaidSuccess] = useState(false);
  const [plaidFinished, setPlaidFinished] = useState(false);
  const oauthStateid = useSelector(({ ui }) => ui.oauth_state_id);
  const { id, pageName } = useSelector(({ page, refs }) => ({
    id: refs.appRequestId,
    pageName: page.name,
  }));
  const [plaidRef, runPlaid, isPlaidEnabled] = usePlaid({
    callback: () => {
      setPlaidSuccess(false);
      if (plaidRef) {
        linkVerification(plaidRef, () => setPlaidSuccess(true));
      }
    },
    name,
    onError,
    onEvent(event) {
      if (event === 'HANDOFF') {
        const { metadata } = plaidParams.current;
        fetchAccounts(metadata, () => setPlaidFinished(true));
        setPlaidSuccess(true);
      }
    },
    onExit(err, metadata) {
      const metaData = {};
      if (metadata) {
        metaData.linkSessionId = metadata.link_session_id;
        metaData.status = metadata.status;
        if (metadata.institution) {
          metaData.institutionId = metadata.institution.institution_id;
          metaData.institutionName = metadata.institution.name;
        }
        metaData.requestId = metadata.request_id;
      }
      if (err) {
        metaData.errorCode = err.error_code;
        metaData.errorMessage = err.error_message;
        metaData.errorType = err.error_type;
      }
      setPlaidSuccess(true);
      updateMeta(metaData);
      onBack();
      if (!oauthStateid) {
        localStorage.removeItem('plaidLinkToken');
      }
    },
    onSuccess(publicToken, metadata) {
      plaidParams.current = {
        metadata,
      };
      localStorage.removeItem('plaidLinkToken');
    },
    plaidLinkTokenEndPoint: `${PAGES_ROOT}/${pageName}/${id}/getPlaidLinkToken`,
    setAppLoading,
  });

  useEffect(() => {
    if (!oauthStateid) {
      localStorage.removeItem('plaidLinkToken');
    }

    if (!isPlaidEnabled) {
      setPlaidFinished(true);
    }
  }, [isPlaidEnabled]);

  useEffect(() => {
    runPlaid();
  }, [runPlaid]);

  return (
    <>
      <legend
        className={classnames('grp-label', classes.label)}
        role={!hasFields ? 'presentation' : undefined}
      >
        <RawHtml type="span" className={classnames('desktop', classes.desktopLabel)}>
          {label}
        </RawHtml>
        <RawHtml type="span" className={classnames('mobile', classes.mobileLabel)}>
          {labelMobile}
        </RawHtml>
      </legend>
      <div
        className={classnames('funding-transfer-ext-group-view', classes.fundingButtonRow, {
          hidden: hasNoFields || !plaidFinished,
        })}
      >
        <RawHtml className={classnames('content', classes.content)}>{subText}</RawHtml>

        <ErrorMsg name={name} type="group" />

        <div className="row">
          <div className={classnames('region-fields', classes.fields)}>{children}</div>
          <OutputFunding name="appRequest_totalFundingAmount" groupName={name} />
          <div className={classnames('action-buttons', classes.actionButtons)}>
            {backAllowed && (
              <button
                className={classnames('btn btn-mobile btn-tertiary btn-grp-back mt-5 edit-amount')}
                data-name="btn-grp-back"
                type="button"
                onClick={onBack}
                variant="secondary"
                disabled={loading}
              >
                {backButtonLabel}
              </button>
            )}

            {resources.changeFundingAccountLabel && (
              <>
                <span>|</span>
                <button
                  className={classnames(
                    'btn btn-mobile btn-tertiary btn-grp-back mt-5 change-funding',
                  )}
                  data-name="btn-grp-change-funding"
                  type="button"
                  disabled={loading}
                  onClick={() => {
                    refetchAccounts(plaidRef);
                  }}
                  variant="secondary"
                >
                  {resources.changeFundingAccountLabel}
                </button>
              </>
            )}
          </div>

          <div className={classes.disclosureSubtext}>
            {resources.disclosureTitle && (
              <RawHtml type="strong">{resources.disclosureTitle}</RawHtml>
            )}
            <br />
            {resources.disclosureSubtext && <RawHtml>{resources.disclosureSubtext}</RawHtml>}
          </div>
          <BottomContent className="bottomContent">{bottomContent}</BottomContent>

          <ButtonRowGroup groupName={name} />
        </div>
      </div>
    </>
  );
};

FundingTransferExtBare.propTypes = {
  backAllowed: PropTypes.bool.isRequired,
  backButtonLabel: PropTypes.string,
  bottomContent: PropTypes.string,
  children: PropTypes.node.isRequired,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  fetchAccounts: PropTypes.func.isRequired,
  hasFields: PropTypes.bool,
  hasNoFields: PropTypes.bool.isRequired,
  label: PropTypes.string,
  labelMobile: PropTypes.string,
  linkVerification: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  name: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  plaidConfig: PropTypes.shape({
    clientName: PropTypes.string,
    env: PropTypes.string,
    product: PropTypes.arrayOf(PropTypes.string),
    token: PropTypes.string,
  }),
  refetchAccounts: PropTypes.func.isRequired,
  resources: PropTypes.objectOf(PropTypes.string),
  setAppLoading: PropTypes.func.isRequired,
  subText: PropTypes.string,
  updateMeta: PropTypes.func.isRequired,
};

FundingTransferExtBare.defaultProps = {
  backButtonLabel: '',
  bottomContent: '',
  hasFields: false,
  label: '',
  labelMobile: '',
  loading: false,
  plaidConfig: {},
  resources: {},
  subText: '',
};

const mapStateToProps = ({ groups, page, ui }, { name }) => {
  const group = groups[name];
  return {
    backAllowed: page.backAllowed || Object.keys(groups)[0] !== name,
    backButtonLabel: group.backButtonLabel,
    bottomContent: group.bottomContent,
    hasFields: group?.fields?.length > 0 ?? false,
    hasNoFields: !group.fields,
    label: group.label,
    labelMobile: group.labelMobile,
    loading: ui.appLoading,
    resources: group.resources,
    subText: group.subText,
  };
};

const FundingTransferExtStyled = withStyles(styles)(FundingTransferExtBare);

export default connect(mapStateToProps, {
  fetchAccounts: plaidActions.fetchAccounts,
  linkVerification: plaidActions.linkVerification,
  onBack: () => uiActions.setActiveGroup('FUNDING_AMOUNT'),
  onError: plaidActions.fetchAccountsFailed,
  refetchAccounts: plaidActions.refetchAccounts,
  setAppLoading: uiActions.setAppLoading,
  updateMeta: plaidActions.updateMeta,
})(FundingTransferExtStyled);
